import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keyBy } from 'lodash';
import ReactSelect, { MenuPlacement, MenuPosition } from 'react-select';
import ReactCreatableSelect from 'react-select/creatable';
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'helpers/languages/translations';

export type TSelectOption = { label: string; value: any };

type TRSelect = {
    options: TSelectOption[];
    onChange: (vals: (string | number)[]) => void;
    selectedOptions: (string | number)[];
    disabled?: boolean;
    setAll?: boolean;
    styles?: Record<string, any>;
    menuPlacement?: MenuPlacement;
    menuPosition?: MenuPosition;
    isMulti?: boolean;
    isCreatable?: boolean;
    placeholder?: string;
};

export const formatOptionsForRSelect = (options: Record<string, any>[] = [], label: string, value: string) =>
    options.map((option) => ({ label: option[label], value: option[value] }));

const getSelectedById = (selectedOptions: (string | number)[], options: TSelectOption[]) => {
    const optionsById = keyBy(options, 'value');
    return selectedOptions?.map((option) => ({
        label: optionsById[option]?.label || option.toString(),
        value: optionsById[option]?.value || option,
    }));
};

const RSelect = ({
    options: op,
    onChange,
    selectedOptions,
    disabled,
    setAll,
    styles,
    menuPlacement = 'auto',
    menuPosition = 'fixed',
    isMulti = true,
    isCreatable,
    placeholder = 'Select...',
}: TRSelect) => {
    const translate = useTranslation();

    const [value, setValue] = useState<TSelectOption[]>(getSelectedById(selectedOptions, op) || []);
    const [options, setOptions] = useState<TSelectOption[]>(op);
    const isAllSelected = useRef(value.length === options.length);

    const allOption = { label: translate('all') as string, value: 'all' };

    const formatedOptions = useMemo(() => {
        return setAll ? [allOption, ...options] : options;
    }, [setAll, options]);

    const handleChange = useCallback((val: TSelectOption[], noUpdate?: boolean) => {
        const vals = keyBy(val, 'value');

        isAllSelected.current = val.length === options.length;

        setOptions(vals.all ? [] : op);
        setValue(vals.all ? [allOption] : val);
        !noUpdate && onChange((vals.all ? options : val).map((option) => option.value));
    }, []);

    useEffect(() => {
        handleChange(getSelectedById(selectedOptions, op) || [], true);
    }, [selectedOptions]);

    const Component = isCreatable ? ReactCreatableSelect : ReactSelect;

    const disabledProps = {
        isClearable: false,
        isSearchable: false,
        closeMenuOnSelect: false,
        menuIsOpen: false, // empêche l'ouvertur
        components: {
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            MultiValueRemove: () => null,
        },
    };

    return (
        <Box flexGrow={1} my={2} cursor={disabled ? 'not-allowed' : 'normal'}>
            <Component
                {...(isMulti && disabled ? disabledProps : {})}
                isMulti={isMulti}
                closeMenuOnSelect={false}
                options={formatedOptions}
                value={isAllSelected.current && !isCreatable ? [allOption] : value}
                onChange={(values) => handleChange(values as any, false)}
                isDisabled={!isMulti && disabled}
                menuPlacement={menuPlacement}
                menuPosition={menuPosition}
                placeholder={placeholder}
                styles={{
                    control: (base) => ({
                        ...base,
                        ...styles,
                        cursor: isMulti && disabled ? 'not-allowed' : 'normal',
                    }),
                    valueContainer: (base) => ({
                        ...base,
                        maxHeight: '100px',
                        overflow: 'auto',
                    }),
                }}
            />
        </Box>
    );
};

export default RSelect;
